import React, { useMemo } from "react";
import { LayoutGrid, Sparkles } from "lucide-react";

import ModuleCard from "../cards/ModuleCard";
import ModuleSummary from "../cards/ModuleSummary";

import {
  calculateTotal,
  toggleAllChildModules,
  toggleAllSubModules,
  toggleBaseModule,
  toggleChildModule,
  toggleSubModule,
} from "../moduleSelectionHelpers";

const StepModulesSelection = ({ modules = [], formData, setFormData }) => {
  const subscribedBaseModuleDetails =
    formData?.SUBSCRIBED_BASEMODULE_DETAILS || [];

  // =========================
  // FILTER MODULES (MEMOIZED)
  // =========================
  const { coreModules, addonModules } = useMemo(() => {
    return {
      coreModules: modules.filter((item) => item.BASEMODULE_TYPE === "CORE"),
      addonModules: modules.filter((item) => item.BASEMODULE_TYPE === "ADDON"),
    };
  }, [modules]);

  console.log("coreModules: ", coreModules);
  // =========================
  // SELECTED CORE MODULE
  // =========================
  const selectedCoreModuleId = useMemo(() => {
    return subscribedBaseModuleDetails.find((selected) =>
      coreModules.some((core) => core.BASEMODULE_ID === selected.BASEMODULE_ID),
    )?.BASEMODULE_ID;
  }, [subscribedBaseModuleDetails, coreModules]);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950">
      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-6 space-y-8 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700">
        {/* HEADER */}
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 py-5 shadow-sm">
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-500/10 to-violet-500/10 blur-3xl rounded-full" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 text-white shadow-lg">
                <LayoutGrid className="w-5 h-5" />
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                  Select Modules
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Choose a core module and customize your platform with add-ons.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 text-xs font-medium">
                <Sparkles className="w-3.5 h-3.5" />1 Core Module Required
              </div>

              <div className="px-3 py-1.5 rounded-xl bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 text-xs font-medium">
                Unlimited Add-ons
              </div>
            </div>
          </div>
        </div>

        {/* CORE MODULES */}
        {coreModules.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 rounded-xl bg-teal-100 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 text-xs font-bold uppercase tracking-wider">
                Core Modules
              </div>

              <div className="flex-1 h-px bg-gradient-to-r from-teal-200 dark:from-teal-500/30 to-transparent" />

              <span className="text-xs text-slate-500 dark:text-slate-400">
                Select One
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {coreModules.map((module) => (
                <ModuleCard
                  key={module.BASEMODULE_ID}
                  module={module}
                  type="core"
                  subscribedBaseModuleDetails={subscribedBaseModuleDetails}
                  selectedCoreModuleId={selectedCoreModuleId}
                  toggleBaseModule={() =>
                    toggleBaseModule(module, formData, setFormData, modules)
                  }
                  toggleChildModule={(baseModuleId, child) =>
                    toggleChildModule(
                      baseModuleId,
                      child,
                      formData,
                      setFormData,
                    )
                  }
                  toggleSubModule={(baseModuleId, moduleId, sub) =>
                    toggleSubModule(
                      baseModuleId,
                      moduleId,
                      sub,
                      formData,
                      setFormData,
                    )
                  }
                  toggleAllChildModules={() =>
                    toggleAllChildModules(module, formData, setFormData)
                  }
                  toggleAllSubModules={(baseModuleId, child) =>
                    toggleAllSubModules(
                      baseModuleId,
                      child,
                      formData,
                      setFormData,
                    )
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* ADDONS */}
        {addonModules.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 rounded-xl bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 text-xs font-bold uppercase tracking-wider">
                Add-on Modules
              </div>

              <div className="flex-1 h-px bg-gradient-to-r from-violet-200 dark:from-violet-500/30 to-transparent" />

              <span className="text-xs text-slate-500 dark:text-slate-400">
                Optional
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {addonModules.map((module) => (
                <ModuleCard
                  key={module.BASEMODULE_ID}
                  module={module}
                  type="addon"
                  subscribedBaseModuleDetails={subscribedBaseModuleDetails}
                  selectedCoreModuleId={selectedCoreModuleId}
                  toggleBaseModule={() =>
                    toggleBaseModule(module, formData, setFormData, modules)
                  }
                  toggleChildModule={(baseModuleId, child) =>
                    toggleChildModule(
                      baseModuleId,
                      child,
                      formData,
                      setFormData,
                    )
                  }
                  toggleSubModule={(baseModuleId, moduleId, sub) =>
                    toggleSubModule(
                      baseModuleId,
                      moduleId,
                      sub,
                      formData,
                      setFormData,
                    )
                  }
                  toggleAllChildModules={() =>
                    toggleAllChildModules(module, formData, setFormData)
                  }
                  toggleAllSubModules={(baseModuleId, child) =>
                    toggleAllSubModules(
                      baseModuleId,
                      child,
                      formData,
                      setFormData,
                    )
                  }
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* SUMMARY */}
      <ModuleSummary
        subscribedBaseModuleDetails={subscribedBaseModuleDetails}
        total={calculateTotal(subscribedBaseModuleDetails, modules)}
      />
    </div>
  );
};

export default StepModulesSelection;
